import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import endpoints from '../../define/endpoints';
import ModalDrawer from './ModalDrawer';
import PropTypes from 'prop-types';
import { Loader } from '../common';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { RadioList } from './RadioList';
import { BetaTextInput } from '../BetaTextInput';
import { useForm } from 'react-hook-form';
import { MegaSelect } from './MegaSelect';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { TRANSLATIONS } from '../../../translations';
import { BREAKPOINTS } from '../../../constants';
import { ModalOrderCard } from './subComponents/ModalOrderCard';
import Dialog, { DialogAlignments, DialogTypes } from '../Dialog/Dialog';
import Typography, {
  TypographyStates,
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import { ModalBookingCompletedReschedule } from './ModalBookingCompletedReschedule';
import { ModalBookingCompletedToVoucher } from './ModalBookingCompletedToVoucher';
import { ModalDrawerResponse } from './ModalDrawerResponse';
import { ModalDrawerExitFlow } from './ModalDrawerExitFlow';
import { getActionlist } from '../../hooks/useOrderListUtils';

const CANCELLATION_MOTIVATION = 'cancellation_motivation';
const CUSTOM_MOTIVATION = 'custom_motivation';
const MOTIVATION = 'motivation';
const modalTitle = TRANSLATIONS.REFUND_REQUEST;
const RESCHEDULE = 'reschedule';

const ModalBookingCompletedRefund = ({
  isOpen,
  setIsOpen,
  token,
  order,
  setRequestDetails,
}) => {
  if (!order || !token) return null;

  const orderId = order.id;
  const hasMounted = useRef(false);

  const isDeviceSmallerThanMblLandscape = !useMatchesMinWidth(
    BREAKPOINTS.MBL_LANDSCAPE
  );
  const { register, handleSubmit } = useForm();
  const [step, setStep] = useState(1);
  const [motivation, setMotivation] = useState(null);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [openTransformModal, setOpenTransformModal] = useState(false);
  const [openRefundModal, setOpenRefundModal] = useState(false);
  const [responseModal, setResponseModal] = useState({
    isOpen: false,
    isSuccess: false,
    responseContent: '',
    modalTitle: '',
  });
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [action, setAction] = useState(null);
  const [radioListError, setRadioListError] = useState(null);
  const [motivationError, setMotivationError] = useState(null);
  const [megaSelectError, setMegaSelectError] = useState(null);
  const [showModalExit, setShowModalExit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('noscroll', isOpen);
    return () => document.body.classList.remove('noscroll');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      resetErrors();
      setShowModalExit(false);
      setRadioListError(null);
      setMegaSelectError(null);
      setMotivationError(null);
    }
  }, [isOpen]);

  // reset isOpen status if the action is interrupted in any of these flows
  useEffect(() => {
    if (hasMounted.current) {
      if (!openRescheduleModal && !openTransformModal && !openRefundModal) {
        setIsOpen(false);
      }
    } else {
      hasMounted.current = true;
    }
  }, [openRescheduleModal, openTransformModal, openRefundModal]);

  const handleGoBack = () => {
    setStep(step - 1);
  };

  const motivationslist = useMemo(
    () => ({
      cancellation_motivation_1:
        TRANSLATIONS.ACTIVITY_WAS_CANCELLED_DUE_TO_THE_WEATHER,
      cancellation_motivation_2: TRANSLATIONS.GUIDE_DID_NOT_SHOW_UP,
      other: TRANSLATIONS.OTHER,
    }),
    []
  );

  const actionsList = getActionlist(order);
  if (actionsList[RESCHEDULE]) {
    actionsList[RESCHEDULE].recommended = true;
  }

  const onSubmit = async () => {
    try {
      setIsLoading(true);

      const { data } = await axios.post(
        `${endpoints.core}/order-customer-request/store`,
        {
          order_id: orderId,
          type: 'completed_booking_refund',
          reason: motivation,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let requestDetails = data && data.order ? data.order : null;
      setRequestDetails(requestDetails);
      setResponseModal({
        isOpen: true,
        isSuccess: true,
        responseContent:
          TRANSLATIONS.YOUR_REFUND_REQUEST_HAS_BEEN_SUCCESSFULLY_SUBMITTED,
        modalTitle: modalTitle,
      });
    } catch (error) {
      // console.log(error);
      setResponseModal({
        isOpen: true,
        isSuccess: false,
        responseContent:
          TRANSLATIONS.UNEXPECTED_ERROR_HAS_OCCURRED_PLEASE_RETRY_LATER,
        modalTitle: modalTitle,
      });
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  const proceed = () => {
    if (step === 1) {
      if ((!motivation || motivation === '') && !showCustomInput) {
        setRadioListError(TRANSLATIONS.RADIO_LIST_ERROR);
        return;
      } else if ((!motivation || motivation === '') && showCustomInput) {
        setMotivationError(TRANSLATIONS.MOTIVATION_ERROR);
        return;
      }
    }
    if (step === 2) {
      if (!action) {
        setMegaSelectError(TRANSLATIONS.RADIO_LIST_ERROR);
        return;
      } else {
        setMegaSelectError(null);
        setOpenRescheduleModal(action === 'reschedule');
        setOpenTransformModal(action === 'transformToVoucher');
        setOpenRefundModal(action === 'refund');
      }
    }

    setStep(step + 1);
  };

  const form = (
    <form>
      <div className="pb-2xs">
        <RadioList
          inputName={CANCELLATION_MOTIVATION}
          options={motivationslist}
          defaultValue={null}
          onChange={(value) => {
            setMotivation(value);
            if (value === 'other') {
              setShowCustomInput(true);
              setMotivation('');
            } else {
              setShowCustomInput(false);
              setMotivation(motivationslist[value]);
            }
          }}
          oneColumn={true}
          error={radioListError}
          setError={setRadioListError}
        />
      </div>
      {showCustomInput && (
        <>
          <BetaTextInput
            asTextArea
            className="modal-ord-li-review__review-form__field"
            label={TRANSLATIONS.TELL_US_YOUR_MOTIVATION}
            name={CUSTOM_MOTIVATION}
            setRef={register({ required: false, maxLength: { value: 500 } })}
            defaultValue={''}
            onChange={(event) => {
              // const value = event.target.value.replace(
              //   /[^a-zA-Z0-9\s.,!?'-]/g,
              //   ''
              // );
              const value = event.target.value;
              setMotivation(value);
            }}
            resize={true}
            limit={500}
            error={motivationError}
            setError={setMotivationError}
            errorMessage={TRANSLATIONS.MOTIVATION_ERROR}
          />
        </>
      )}
    </form>
  );

  const resetErrors = () => {
    setTimeout(() => {
      setRadioListError(null);
      setMotivationError(null);
    }, 500);
  };

  const firstStepModal = (
    <ModalDrawer
      isOpen={isOpen && step === 1 && !showModalExit}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
          <div>
            <Typography
              classNames="text-left"
              variant={TypographyVariants.p2}
              weight={TypographyWeights.bold}
            >
              {TRANSLATIONS.WE_ARE_SORRY_YOUR_ADVENTURE_DID_NOT_GO_AS_PLANNED}
            </Typography>
            <Typography classNames="text-left" variant={TypographyVariants.p3}>
              {
                TRANSLATIONS.HELP_US_BETTER_UNDERSTAND_WHAT_HAPPENED_PLEASE_SELECT_THE_REASON
              }
            </Typography>
          </div>
          {form}
          <Typography
            classNames="text-left"
            variant={TypographyVariants.p4}
            state={TypographyStates['neutral-light']}
          >
            {
              TRANSLATIONS.WE_APPRECIATE_YOUR_FEEDBACK_EVERY_REPORT_HELPS_US_IMPROVE
            }
          </Typography>
        </div>
      }
      footer={
        <div className="d-flex gap-m pb-m pl-xl pr-xl">
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={TRANSLATIONS.CANCEL}
            onClick={() => {
              setShowModalExit(true);
            }}
          />
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.secondary}
            label={TRANSLATIONS.CONTINUE}
            onClick={() => proceed()}
          />
        </div>
      }
      handleAfterClose={resetErrors}
    />
  );

  const secondStepModal = (
    <ModalDrawer
      goBack={() => {
        setStep(step - 1);
      }}
      isOpen={isOpen && step === 2 && !showModalExit}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
          <ModalOrderCard order={order} noBorder />
          <Typography
            classNames="text-left"
            variant={TypographyVariants.p2}
            weight={TypographyWeights.bold}
          >
            {
              TRANSLATIONS.SOMETIMES_A_SECOND_ATTEMPT_IS_ENOUGH_CHOOSE_ONE_OF_THE_OPTIONS
            }
          </Typography>
          <MegaSelect
            options={actionsList}
            defaultValue={null}
            onChange={(value) => {
              if (value === action) {
                setAction(null);
              } else {
                setAction(value);
              }
            }}
            error={megaSelectError}
            setError={setMegaSelectError}
          />
        </div>
      }
      footer={
        <div className="d-flex gap-m pb-m pl-xl pr-xl">
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={TRANSLATIONS.CANCEL}
            onClick={() => setShowModalExit(true)}
          />
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.secondary}
            label={TRANSLATIONS.CONTINUE}
            onClick={() => proceed()}
          />
        </div>
      }
    />
  );

  const completeRefundRequest = (
    <ModalDrawer
      goBack={() => {
        setStep(step - 1);
      }}
      isOpen={openRefundModal && !showModalExit}
      setIsOpen={setOpenRefundModal}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
          {isLoading ? (
            <Loader loading={isLoading} />
          ) : (
            <>
              <Dialog
                align={DialogAlignments.row}
                type={DialogTypes.feature}
                content={TRANSLATIONS.HOW_DOES_THE_REFUND_REQUEST_WORK}
                icon={
                  <BetaIcon
                    name={'info'}
                    color={IconColors.regular}
                    size={IconSizes[24]}
                  />
                }
              />
              <div className="d-flex flex-column gap-xs">
                <div
                  className="typography__p3 typography__neutral text-left"
                  dangerouslySetInnerHTML={{
                    __html:
                      TRANSLATIONS.RECAP_OF_THE_REFUND_REQUEST_CONFIRM_THE_FOLLOWING_DETAILS,
                  }}
                />
                <ModalOrderCard
                  order={order}
                  toBeRefunded={true}
                  motivation={motivation}
                />
              </div>
            </>
          )}
        </div>
      }
      footer={
        <div className="d-flex gap-m pb-m pl-xl pr-xl">
          <Button
            disabled={isLoading}
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={TRANSLATIONS.CANCEL}
            onClick={() => setShowModalExit(true)}
          />
          <Button
            disabled={isLoading}
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.secondary}
            label={TRANSLATIONS.CONFIRM}
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      }
    />
  );

  const voucherModal = (
    <ModalBookingCompletedToVoucher
      isOpen={openTransformModal}
      setIsOpen={setOpenTransformModal}
      order={order}
      token={token}
      motivation={motivation}
      customGoBackFunc={handleGoBack}
      setResponseModal={setResponseModal}
      setRequestDetails={setRequestDetails}
    />
  );

  const modalRescheduleOrder = (
    <ModalBookingCompletedReschedule
      isOpen={openRescheduleModal}
      setIsOpen={setOpenRescheduleModal}
      order={order}
      token={token}
      motivation={motivation}
      customGoBackFunc={handleGoBack}
      setResponseModal={setResponseModal}
      setRequestDetails={setRequestDetails}
    />
  );

  const responseModalDrawer = (
    <ModalDrawerResponse
      isOpen={responseModal.isOpen}
      setIsOpen={setResponseModal}
      isSuccess={responseModal.isSuccess}
      modalTitle={responseModal.modalTitle}
      responseContent={responseModal.responseContent}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
    />
  );

  const exitFlowModal = (
    <ModalDrawerExitFlow
      isOpen={showModalExit}
      setIsOpen={setShowModalExit}
      closeAction={() => setIsOpen(false)}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
    />
  );

  return (
    <>
      {firstStepModal}
      {secondStepModal}
      {step === 3 && openRescheduleModal && modalRescheduleOrder}
      {step === 3 && openTransformModal && voucherModal}
      {step === 3 && openRefundModal && completeRefundRequest}
      {exitFlowModal}
      {responseModal.isOpen && responseModalDrawer}
    </>
  );
};

ModalBookingCompletedRefund.propTypes = {};

export { ModalBookingCompletedRefund };
